import { roomBookingsKeys } from "@/modules/room-bookings/presentation/api/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type { CreditInvoicesListQuery, ReceiptsSummaryQuery } from "./client";
import { billingApi } from "./client";

export const receiptKeys = {
  all: ["billing", "receipts"] as const,
  list: (query: OffsetPageQueryDTO) =>
    ["billing", "receipts", "list", query] as const,
  summary: (query: ReceiptsSummaryQuery) =>
    ["billing", "receipts", "summary", query] as const,
  detail: (id: string) => ["billing", "receipts", "detail", id] as const,
  amendContext: (id: string) =>
    ["billing", "receipts", "amend-context", id] as const,
  voidPreview: (id: string) =>
    ["billing", "receipts", "void-preview", id] as const,
};

export const creditInvoiceKeys = {
  all: ["billing", "credit-invoices"] as const,
  list: (query: CreditInvoicesListQuery) =>
    ["billing", "credit-invoices", "list", query] as const,
  detail: (id: string) => ["billing", "credit-invoices", "detail", id] as const,
  voidPreview: (id: string) =>
    ["billing", "credit-invoices", "void-preview", id] as const,
};

export function useReceiptsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: receiptKeys.list(query),
    queryFn: () => billingApi.receipts.list(query),
  });
}

export function useReceiptsSummaryQuery(query: ReceiptsSummaryQuery) {
  return useQuery({
    queryKey: receiptKeys.summary(query),
    queryFn: () => billingApi.receipts.getSummary(query),
  });
}

export function useReceiptQuery(id: string | null) {
  return useQuery({
    queryKey: receiptKeys.detail(id ?? ""),
    queryFn: () => billingApi.receipts.getById(id!),
    enabled: !!id,
  });
}

export function useVoidReceiptPreviewQuery(
  receiptId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: receiptKeys.voidPreview(receiptId ?? ""),
    queryFn: () => billingApi.receipts.getVoidPreview(receiptId!),
    enabled: !!receiptId && enabled,
  });
}

export function useVoidReceiptMutation(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      billingApi.receipts.voidReceipt(receiptId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: receiptKeys.detail(receiptId) });
      void qc.invalidateQueries({ queryKey: receiptKeys.all });
      void qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
    },
  });
}

export function useReceiptRoomBookingAmendContextQuery(
  receiptId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: receiptKeys.amendContext(receiptId ?? ""),
    queryFn: () => billingApi.receipts.getRoomBookingAmendContext(receiptId!),
    enabled: !!receiptId && enabled,
  });
}

export function useAmendReceiptRoomBookingAssistMutation(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      items: Array<{ instructorId: string; pricePerSession: number }>;
    }) => billingApi.receipts.amendRoomBookingAssistItems(receiptId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: receiptKeys.detail(receiptId) });
      void qc.invalidateQueries({
        queryKey: receiptKeys.amendContext(receiptId),
      });
      void qc.invalidateQueries({ queryKey: receiptKeys.all });
      void qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
  });
}

export function useAmendReceiptRoomBookingItemsMutation(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      cancelBookingIds?: string[];
      assistItems?: Array<{ instructorId: string; pricePerSession: number }>;
      syncFromCalendar?: boolean;
    }) => billingApi.receipts.amendRoomBookingItems(receiptId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: receiptKeys.detail(receiptId) });
      void qc.invalidateQueries({
        queryKey: receiptKeys.amendContext(receiptId),
      });
      void qc.invalidateQueries({ queryKey: receiptKeys.all });
      void qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
  });
}

export function useCreditInvoicesQuery(query: CreditInvoicesListQuery) {
  return useQuery({
    queryKey: creditInvoiceKeys.list(query),
    queryFn: () => billingApi.creditInvoices.list(query),
  });
}

export function useCreditInvoiceQuery(id: string | null) {
  return useQuery({
    queryKey: creditInvoiceKeys.detail(id ?? ""),
    queryFn: () => {
      if (!id) throw new Error("missing id");
      return billingApi.creditInvoices.getById(id);
    },
    enabled: !!id,
  });
}

export function useApplyCreditPaymentMutation(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; notes?: string }) => {
      if (!invoiceId) throw new Error("missing invoice id");
      return billingApi.creditInvoices.applyPayment(invoiceId, body);
    },
    onSuccess: (data) => {
      void qc.setQueryData(creditInvoiceKeys.detail(invoiceId), data);
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
    },
  });
}

export function useReconcileCreditPaymentMutation(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      billingApi.creditInvoices.reconcilePayment(paymentId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: creditInvoiceKeys.detail(invoiceId),
      });
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
    },
  });
}

export function useVoidCreditInvoicePreviewQuery(
  invoiceId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: creditInvoiceKeys.voidPreview(invoiceId ?? ""),
    queryFn: () => billingApi.creditInvoices.getVoidPreview(invoiceId!),
    enabled: !!invoiceId && enabled,
  });
}

export function useVoidCreditInvoiceMutation(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      billingApi.creditInvoices.voidInvoice(invoiceId, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: creditInvoiceKeys.detail(invoiceId),
      });
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
      void qc.invalidateQueries({ queryKey: receiptKeys.all });
      void qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
  });
}

export function useBulkVoidCreditInvoicesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { invoiceIds: string[]; reason: string }) =>
      billingApi.creditInvoices.bulkVoid(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
      void qc.invalidateQueries({ queryKey: receiptKeys.all });
      void qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
  });
}

export function useVoidCreditPaymentMutation(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { paymentId: string; reason: string }) =>
      billingApi.creditInvoices.voidPayment(params.paymentId, {
        reason: params.reason,
      }),
    onSuccess: (data) => {
      void qc.setQueryData(creditInvoiceKeys.detail(invoiceId), data);
      void qc.invalidateQueries({ queryKey: creditInvoiceKeys.all });
    },
  });
}
